import React, { useState, useCallback } from 'react';
import { Box, Typography } from '@lucid/lego';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { createTechSupportFormFields } from './formFields';
import { useStore } from '../../../store/store';
import { useCreateTechSupportCase } from '../../../api/services/lupp/fse/fse';
import {
  CreateTechSupportCaseRequest,
  CreateTechSupportCaseResponse,
} from '../../../api/services/lupp/fse/dto.fse';
import VinSearch from '../../../components/VinSearch/VinSearch';
import type { VehicleData } from '../../../components/VinSearch/types';
import styles from './CreateTechSupport.module.scss';

const REDIRECT_DELAY = 5000;
const SUCCESS_MESSAGE = 'Tech Support Case created successfully!';
const VALIDATION_ERROR = 'Please fix the form errors before submitting.';
const API_ERROR = 'Failed to create tech support case. Please try again.';
const REDIRECT_PATH = '/service-center/fse-tech-support';

const CreateTechSupport: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const navigate = useLocaleNavigate();
  const { mutate: createCase, isPending } = useCreateTechSupportCase();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleFormSubmit = useCallback(
    (formData: unknown, selectedAsset: VehicleData) => {
      setErrorMessage(null);
      setSuccessMessage('');

      const data = formData as Record<string, unknown>;

      const payload: CreateTechSupportCaseRequest = {
        customerIdentifier: selectedAsset.customerIdentifier,
        vehicleIdentifier: selectedAsset.vehicleIdentifier,
        caseType: (data.caseType as string) || '',
        priority: (data.priority as string) || '',
        componentList: (data.componentList as string[]) || [],
        description: (data.description as string) || '',
        safetyCheckVerificationList: (data.safetyCheckVerificationList as string[]) || [],
        customerName: (data.customerName as string) || selectedAsset.customer.name || '',
        vin: (data.vin as string) || selectedAsset.details.vin || '',
        customerPhone: (data.customerPhone as string) || selectedAsset.customer.phone || '',
        customerEmail: (data.customerEmail as string) || selectedAsset.customer.email || '',
        workOrderNumber: (data.workOrderNumber as string) || '',
        status: 'OPEN',
      };

      createCase(payload, {
        onSuccess: (response: CreateTechSupportCaseResponse) => {
          setSuccessMessage(
            `${SUCCESS_MESSAGE} Case-${response.techSupportIdentifier?.slice(-12)}`
          );
          setTimeout(() => {
            navigate(REDIRECT_PATH);
          }, REDIRECT_DELAY);
        },
        onError: (error) => {
          console.error('API error:', error);
          setErrorMessage(API_ERROR);
        },
      });
    },
    [createCase, navigate]
  );

  const handleFormError = useCallback(() => {
    setErrorMessage(VALIDATION_ERROR);
    setSuccessMessage('');
  }, []);

  const handleSliderClose = useCallback(() => {
    setErrorMessage(null);
    setSuccessMessage('');
  }, []);

  // Generate form fields dynamically with vehicle data pre-populated
  const getFormFields = useCallback((selectedAsset: VehicleData) => {
    return createTechSupportFormFields(undefined, {
      vin: selectedAsset.details.vin,
      customerName: selectedAsset.customer.name,
      customerPhone: selectedAsset.customer.phone,
      customerEmail: selectedAsset.customer.email,
    });
  }, []);

  return (
    <Box className={styles.pageContainer}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        Tech Support Case
      </Typography>
      <Box pt={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          Search for a vehicle to create a new tech support case
        </Typography>
      </Box>
      <Box mt={32}>
        <VinSearch
          theme={theme}
          label="Locate Asset"
          placeholder="Search: VIN / Name / Tel No. / Licence Plate ..."
          emptyMessage="No vehicles found matching your search"
          dataTestId="tech-support-vin-search"
          sliderTitle="Create Tech Support Case"
          getFormFields={getFormFields}
          formTitle="Case Details"
          formId="create-tech-support-form"
          isSubmitting={isPending}
          successMessage={successMessage}
          errorMessage={errorMessage}
          onFormSubmit={handleFormSubmit}
          onFormError={handleFormError}
          onSliderClose={handleSliderClose}
        />
      </Box>
    </Box>
  );
};

export default CreateTechSupport;
